import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { devTools } from '@ngneat/elf-devtools';
import { provideTransloco, translocoConfig } from '@ngneat/transloco';
import { appRoutes } from 'src/app/app.routes';
import { SandozDateAdapter } from '@app-core/adapters/sandoz-date-adapter';
import { InitializationService } from 'src/app/initialization/initialization.service';
import { LanguageCode } from '@app-shared/enums/language-codes.enum';
import {
  TranslocoHttpLoader,
  translocoAppConfig,
} from 'src/app/transloco/transloco-loader';
import { environment } from 'src/environments/environment';
import { NGQ_LANGUAGES_TOKEN } from '@app-core/injection-tokens/languages.token';
import { tokenInterceptor } from '@app-core/interceptors/token.interceptor';
import { serverErrorInterceptor } from '@app-core/interceptors/server-error.interceptor';
import { countryAndLanguageInterceptor } from '@app-core/interceptors/country-and-language.interceptor';

if (!environment.production) {
  devTools();
}

export function appInitializations(initService: InitializationService) {
  return (): Promise<void> => {
    return initService.init();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        tokenInterceptor,
        serverErrorInterceptor,
        countryAndLanguageInterceptor,
      ]),
    ),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),

    provideTransloco({
      config: translocoConfig(translocoAppConfig),
      loader: TranslocoHttpLoader,
    }),

    importProvidersFrom([
      MatSnackBarModule, // we need this here as we use the snackbar in the interceptor,
      MatNativeDateModule,
    ]),

    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
      } as MatFormFieldDefaultOptions,
    },

    {
      provide: NGQ_LANGUAGES_TOKEN,
      useValue: [
        {
          id: LanguageCode.EN_GB,
          title: 'English',
          flag: 'gb',
        },
        {
          id: LanguageCode.DE_DE,
          title: 'Deutsch',
          flag: 'de',
        },
        {
          id: LanguageCode.FR_FR,
          title: 'Français',
          flag: 'fr',
        },
        {
          id: LanguageCode.IT_IT,
          title: 'Italiano',
          flag: 'it',
        },
        {
          id: LanguageCode.ES_ES,
          title: 'Español',
          flag: 'es',
        },
        {
          id: LanguageCode.DE_CH,
          title: 'Deutsch (Schweiz)',
          flag: 'ch',
        },
        {
          id: LanguageCode.CS_CZ,
          title: 'Čeština',
          flag: 'cz',
        },
        {
          id: LanguageCode.NL_BE,
          title: 'Nederlands',
          flag: 'nl',
        },
      ],
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'de-DE',
    },
    // overrides getDateNames() method from NativeDateAdapter class
    {
      provide: DateAdapter,
      useClass: SandozDateAdapter,
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },

    // code that needs to run before app is loaded
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializations,
      deps: [InitializationService],
      multi: true,
    },
  ],
};
